/** Armazon movil v2: cabecera de marca, titulo en contenido y navegacion inferior. */
import { useState } from 'react';
import { clsx } from 'clsx';
import { NavLink } from 'react-router-dom';
import { bottomTabs, visibleTabs } from '@/config/mobileMenu';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { AccountSheet } from '@/components/AccountSheet';
import { MobileIcon } from '@/components/MobileIcon';
import { Avatar } from '@/components/Avatar';
import { useTenantLogo } from '@/hooks/useTenantLogo';
import { useMobileAuth } from '@/state/MobileAuthContext';
import type { ReactNode } from 'react';

type MobileLayoutProps = {
  children: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  showBack?: boolean;
};

export function MobileLayout({
  children,
  title,
  subtitle,
  action,
  showBack = false,
}: MobileLayoutProps) {
  const { user, tenantName, tenantLogoUrl, tenantLogoAlt, hasPermission } = useMobileAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const tabs = visibleTabs(bottomTabs, hasPermission);
  const { objectUrl: headerLogoUrl } = useTenantLogo(
    user?.active_tenant_id ?? null,
    tenantLogoUrl,
  );
  const hasLogo = !!headerLogoUrl && !!user?.active_tenant_id;
  const brandName = tenantName ?? 'ERPA Móvil';

  return (
    <div className="flex min-h-dvh flex-col bg-app">
      <OfflineIndicator />
      {/* Cabecera v2: izquierda avatar o atras, centro marca, derecha accion o espaciador */}
      <header
        className={clsx(
          'sticky top-0 z-40 border-b border-border-light bg-white',
          'safe-area-top',
        )}
      >
        <div className="flex items-center gap-3 px-4 py-2.5">
          {showBack ? (
            <button
              type="button"
              onClick={() => window.history.back()}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all hover:bg-bg-tertiary active:scale-95"
              aria-label="Volver"
            >
              <MobileIcon name="arrow_back" className="h-6 w-6 text-text-secondary" />
            </button>
          ) : user ? (
            <button
              type="button"
              onClick={() => setAccountOpen(true)}
              aria-label={`Cuenta de ${user.full_name}`}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all hover:bg-bg-tertiary active:scale-95"
            >
              <Avatar name={user.full_name} size={32} />
            </button>
          ) : (
            <span className="h-8 w-8 shrink-0" />
          )}

          <div className="flex min-w-0 flex-1 items-center justify-center gap-2">
            {user?.active_tenant_id &&
              (hasLogo ? (
                <img
                  src={headerLogoUrl!}
                  alt={tenantLogoAlt}
                  className="h-9 w-9 shrink-0 rounded-md border border-border-light bg-white object-contain p-0.5"
                />
              ) : (
                <span
                  role="img"
                  aria-label={tenantName ? `Logo de ${tenantName}` : 'Logo de la empresa'}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-light text-sm font-bold text-primary"
                >
                  {brandName.trim().charAt(0).toUpperCase()}
                </span>
              ))}
            <span title={brandName} className="min-w-0 truncate text-sm font-semibold text-text-primary">
              {brandName}
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {action ? <div className="shrink-0">{action}</div> : null}
            <img
              src="/rpa-value-logo.png"
              alt="RPA Value"
              className="h-4 w-auto max-w-[80px]"
            />
          </div>
        </div>
      </header>

      {/* Contenido sin animacion por navegacion; el titulo de pagina vive aqui */}
      <main className="flex-1 px-4 py-4 pb-28">
        {title !== '' && (
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-text-heading">{title}</h1>
            {subtitle ? (
              <p className="mt-0.5 text-sm text-text-secondary">{subtitle}</p>
            ) : null}
          </div>
        )}
        {children}
      </main>

      <AccountSheet open={accountOpen} onClose={() => setAccountOpen(false)} />

      {/* Navegacion inferior con aire y area segura */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white shadow-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Navegacion principal"
      >
        <div
          className="mx-auto grid max-w-md gap-1 px-3 pb-2 pt-2"
          style={{ gridTemplateColumns: `repeat(${Math.max(tabs.length, 1)}, 1fr)` }}
        >
          {tabs.map((tab) => (
            <NavLink
              key={tab.key}
              to={tab.path}
              className={({ isActive }) => clsx(
                'flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl px-2 py-2',
                'transition-colors active:scale-95',
                isActive ? 'text-primary' : 'text-text-tertiary hover:text-text-secondary',
              )}
            >
              {({ isActive }) => (
                <>
                  <span
                    className={clsx(
                      'flex h-10 w-10 items-center justify-center rounded-full transition-colors',
                      isActive && 'bg-primary-light',
                    )}
                  >
                    <MobileIcon
                      name={tab.icon}
                      className={clsx(isActive ? 'h-7 w-7' : 'h-6 w-6')}
                    />
                  </span>
                  <span
                    className={clsx(
                      'max-w-full truncate text-xs',
                      isActive ? 'font-bold' : 'font-medium',
                    )}
                  >
                    {tab.label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
